<template>
  <div class="message-queue">
    <br />
    <h1 class="text-xs-center">Message Queue</h1>
    <form @submit.prevent="submitSearch">
      <v-layout row class="message-queue-search">
        <v-flex xs12 md4 px-2>
          <v-text-field
            solo
            clearable
            hide-details
            v-model="searchRecipient"
            append-icon="fal fa-search"
            :disabled="apiInProgress"
            type="search"
            placeholder="Search by phone or email"
            @keyup.enter="submitSearch"
            @click:append="submitSearch"
          />
        </v-flex>
      </v-layout>
      <v-layout row wrap class="message-queue-filters">
        <v-flex xs12 sm6 md2 px-2 pb-2>
          <v-select
            :items="typeOptions"
            clearable
            label="Type"
            dense
            hide-details
            multiple
            single-line
            placeholder="Select type(s)"
            v-model="selectedTypes"
          >
            <template v-slot:selection="{ item, index }">
              <small v-if="index === 0">{{ item.text }}</small>
              <small
                v-if="index === 1"
                class="grey--text caption pl-2"
              >(+{{ selectedTypes.length - 1 }} others)</small>
            </template>
          </v-select>
        </v-flex>
        <v-flex xs12 sm6 md2 px-2 pb-2>
          <v-select
            :items="statusOptions"
            clearable
            label="Status"
            dense
            hide-details
            multiple
            single-line
            placeholder="Select status"
            v-model="selectedStatuses"
          >
            <template v-slot:selection="{ item, index }">
              <small v-if="index === 0">{{ item.text }}</small>
              <small
                v-if="index === 1"
                class="grey--text caption pl-2"
              >(+{{ selectedStatuses.length - 1 }} others)</small>
            </template>
          </v-select>
        </v-flex>
        <v-flex xs12 sm6 md4 px-2 pb-2 class="message-queue-date-flex">
          <st-date-field
            id="message-queue-date-from"
            v-model="dateFrom"
            label="From"
            hide-details
            with-time
            to-date-id="message-queue-date-to"
          />
        </v-flex>
        <v-flex xs12 sm6 md3 px-2 pb-2 class="message-queue-date-flex">
          <st-date-field
            id="message-queue-date-to"
            v-model="dateTo"
            label="To"
            hide-details
            with-time
            from-date-id="message-queue-date-from"
          />
        </v-flex>
        <v-flex xs12 sm6 md1 px-2 pb-2 class="message-queue-filter-btn">
          <v-btn small fab type="submit"><v-icon small>fal fa-filter</v-icon></v-btn>
        </v-flex>
      </v-layout>
    </form>
    <v-data-table
      :headers="headers"
      :items="messages"
      :pagination.sync="pagination"
      :total-items="totalMessages"
      :loading="apiInProgress"
      :rows-per-page-items="[10, 25, 50]"
      @update:pagination="onPagination"
      class="elevation-1"
      must-sort
    >
      <template v-slot:items="props">
        <tr class="tr--pointer" @click="openDetails(props.item)">
          <td>
            <div class="message-queue-type-cell">
              <v-icon
                v-if="props.item.typeIcon"
                small
                class="mr-1"
                color="primary"
              >{{ props.item.typeIcon }}</v-icon>
              {{ props.item.typeLabel }}
            </div>
          </td>
          <td>{{ props.item.recipientLabel }}</td>
          <td class="message-queue-content-cell">{{ props.item.contentLabel }}</td>
          <td>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon
                  small
                  v-on="on"
                  :color="props.item.statusIconData && props.item.statusIconData.color"
                >{{ props.item.statusIconData && props.item.statusIconData.icon }}</v-icon>
              </template>
              <span>{{ props.item.statusLabel }}</span>
            </v-tooltip>
          </td>
          <td>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span class="message-queue-delivery-cell" v-on="on">
                  <v-icon
                    small
                    class="mr-1"
                    :color="props.item.deliveryStatusIconData && props.item.deliveryStatusIconData.color"
                  >{{ props.item.deliveryStatusIconData && props.item.deliveryStatusIconData.icon }}</v-icon>
                  {{ props.item.deliveryStatusLabel }}
                </span>
              </template>
              <span>{{ props.item.providerStatusLabel }}</span>
            </v-tooltip>
          </td>
          <td>{{ props.item.retryLabel }}</td>
          <td><small>{{ props.item.dateCreatedLabel }}</small></td>
          <td><small>{{ props.item.dateUpdatedLabel }}</small></td>
        </tr>
      </template>
    </v-data-table>
    <message-queue-details-dialog
      v-model="detailsDialogOpen"
      :message="selectedMessage"
    />
  </div>
</template>

<style lang="scss" scoped>
.message-queue-type-cell {
  display: flex;
  align-items: center;
}

.message-queue-content-cell {
  max-width: 280px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-queue-delivery-cell {
  display: inline-flex;
  align-items: center;
}

.message-queue-search {
  margin-bottom: 10px;
}

.message-queue-search ::v-deep .v-input {
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
}

@media (min-width: 960px) {
  .message-queue-filters {
    flex-wrap: nowrap !important;
    align-items: center;
  }

  .message-queue-filters > .flex {
    flex: 1 1 0 !important;
    max-width: none !important;
    width: auto !important;
    min-width: 0;
  }

  .message-queue-filters .message-queue-date-flex {
    flex: 0 0 auto !important;
    width: auto !important;
    min-width: 0;
    max-width: none !important;
    overflow: visible;
  }

  .message-queue-filters .message-queue-filter-btn {
    flex: 0 0 auto !important;
    max-width: none !important;
    width: auto !important;
  }

  .message-queue-filters ::v-deep .st-date-field--datetime,
  .message-queue-filters ::v-deep .st-date-field--datetime .v-input,
  .message-queue-filters ::v-deep .st-date-field--datetime .v-input__control,
  .message-queue-filters ::v-deep .st-date-field--datetime .v-input__slot,
  .message-queue-filters ::v-deep .st-date-field--datetime .v-text-field__slot {
    width: auto;
    max-width: none;
  }

  .message-queue-filters ::v-deep .st-date-field--datetime input[type="datetime-local"] {
    flex: 0 0 auto;
    width: auto;
    min-width: 0;
    max-width: none;
    padding-right: 0;
    field-sizing: content;
  }

  .message-queue-filters ::v-deep .st-date-field--datetime input[type="datetime-local"]::-webkit-calendar-picker-indicator {
    position: static;
    margin: 0 2px 0 6px;
  }

  .message-queue-filters ::v-deep .st-date-field--datetime .v-input__append-inner {
    padding: 0 0 0 2px;
    margin: 0;
  }
}
</style>

<script>
import src from './src'
export default src
</script>
